import React, { useState } from 'react';
import styled, { keyframes } from 'styled-components';
import { Link, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { FaQuoteLeft, FaArrowRight, FaChevronLeft, FaChevronRight, FaCheckCircle, FaStar, FaCheck } from 'react-icons/fa';
import SEO from '../../components/common/SEO';
import theme from '../../theme';

import belenGemuseLogo from '../../assets/images/references/belen_gemuse.png';
import cbGrosshandelLogo from '../../assets/images/references/cbgrosshandel.png';
import dosteliLogo from '../../assets/images/references/dosteli_food.png';
import pasaLogo from '../../assets/images/references/pasa_lebensmittel.png';

const fadeInUp = keyframes`
  from { opacity: 0; transform: translateY(24px); }
  to { opacity: 1; transform: translateY(0); }
`;

const PageContainer = styled.div`
  min-height: 100vh;
`;

const HeroSection = styled.section`
  background: linear-gradient(135deg, ${theme.colors.secondary} 0%, #1a1a2e 100%);
  padding: 120px 5% 80px;
  text-align: center;
  color: white;
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    top: -40%;
    right: -20%;
    width: 600px;
    height: 600px;
    background: radial-gradient(circle, rgba(231, 76, 60, 0.08) 0%, transparent 60%);
    border-radius: 50%;
  }
`;

const HeroInner = styled.div`
  position: relative;
  z-index: 2;
  max-width: 800px;
  margin: 0 auto;
  animation: ${fadeInUp} 0.7s ease-out;
`;

const HeroBadge = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  background: rgba(231, 76, 60, 0.15);
  color: ${theme.colors.primary};
  padding: 0.5rem 1.25rem;
  border-radius: 50px;
  font-size: 0.8rem;
  font-weight: 700;
  letter-spacing: 1.5px;
  margin-bottom: 1.5rem;
  border: 1px solid rgba(231, 76, 60, 0.25);
  text-transform: uppercase;

  svg { font-size: 0.7rem; }
`;

const HeroTitle = styled.h1`
  font-size: 3rem;
  font-weight: 800;
  line-height: 1.15;
  margin-bottom: 1.25rem;

  @media (max-width: ${theme.breakpoints.tablet}) { font-size: 2.2rem; }
  @media (max-width: ${theme.breakpoints.mobile}) { font-size: 1.8rem; }
`;

const HeroSubtitle = styled.p`
  font-size: 1.15rem;
  line-height: 1.7;
  color: rgba(255, 255, 255, 0.75);
  max-width: 600px;
  margin: 0 auto;

  @media (max-width: ${theme.breakpoints.mobile}) { font-size: 1rem; }
`;

const StatsBar = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 1px;
  background: rgba(255, 255, 255, 0.1);
  border-radius: 16px;
  overflow: hidden;
  max-width: 750px;
  margin: 3.5rem auto 0;

  @media (max-width: ${theme.breakpoints.tablet}) {
    grid-template-columns: repeat(2, 1fr);
  }
`;

const StatCard = styled.div`
  padding: 1.5rem 1rem;
  background: rgba(255, 255, 255, 0.05);
  backdrop-filter: blur(10px);
  text-align: center;
`;

const StatNumber = styled.div`
  font-size: 1.75rem;
  font-weight: 800;
  color: ${theme.colors.primary};
  margin-bottom: 0.25rem;
`;

const StatLabel = styled.div`
  font-size: 0.78rem;
  color: rgba(255, 255, 255, 0.55);
  font-weight: 500;
`;

const LogoSection = styled.section`
  padding: 60px 5%;
  background: #ffffff;
  border-bottom: 1px solid #f0f0f0;
`;

const LogoInner = styled.div`
  max-width: 900px;
  margin: 0 auto;
`;

const LogoLabel = styled.p`
  text-align: center;
  font-size: 0.85rem;
  font-weight: 600;
  color: #94a3b8;
  text-transform: uppercase;
  letter-spacing: 2px;
  margin-bottom: 2rem;
`;

const LogoGrid = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 3rem;
  flex-wrap: wrap;

  @media (max-width: ${theme.breakpoints.mobile}) {
    gap: 2rem;
  }
`;

const LogoItem = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.75rem;
  transition: all 0.3s ease;
  opacity: 0.7;

  &:hover {
    opacity: 1;
    transform: scale(1.05);
  }

  img {
    height: 64px;
    max-width: 140px;
    object-fit: contain;
    filter: grayscale(40%);
    transition: filter 0.3s ease;
  }

  &:hover img {
    filter: grayscale(0%);
  }

  @media (max-width: ${theme.breakpoints.mobile}) {
    img { height: 50px; max-width: 110px; }
  }
`;

const LogoName = styled.span`
  font-size: 0.8rem;
  font-weight: 600;
  color: ${theme.colors.secondary};
`;

const LogoCountry = styled.span`
  font-size: 0.7rem;
  color: #94a3b8;
  margin-top: -4px;
`;

const TestimonialsSection = styled.section`
  padding: 80px 5%;
  background: #f8f9fa;
`;

const TestimonialsInner = styled.div`
  max-width: 900px;
  margin: 0 auto;
`;

const SectionHeader = styled.div`
  text-align: center;
  margin-bottom: 3rem;
`;

const SectionTitle = styled.h2`
  font-size: 2rem;
  font-weight: 800;
  color: ${theme.colors.secondary};
  margin-bottom: 0.75rem;

  @media (max-width: ${theme.breakpoints.mobile}) { font-size: 1.5rem; }
`;

const SectionSubtitle = styled.p`
  font-size: 1rem;
  color: ${theme.colors.darkGray};
  max-width: 550px;
  margin: 0 auto;
`;

const TestimonialCarousel = styled.div`
  position: relative;
`;

const TestimonialSlide = styled.div`
  background: #ffffff;
  border-radius: 20px;
  box-shadow: 0 4px 24px rgba(0, 0, 0, 0.06);
  border: 1px solid #e5e7eb;
  position: relative;
  animation: ${fadeInUp} 0.5s ease-out;
  overflow: hidden;
`;

const MicroProofBar = styled.div`
  background: linear-gradient(135deg, ${theme.colors.secondary} 0%, #1a1a2e 100%);
  padding: 1.5rem 2.5rem;
  display: flex;
  flex-direction: column;
  gap: 0.65rem;

  @media (max-width: ${theme.breakpoints.mobile}) {
    padding: 1.25rem 1.5rem;
  }
`;

const MicroProofItem = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  color: #ffffff;
  font-size: 1rem;
  font-weight: 600;
  line-height: 1.4;
  letter-spacing: 0.01em;

  @media (max-width: ${theme.breakpoints.mobile}) {
    font-size: 0.92rem;
  }
`;

const MicroProofIcon = styled.span`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 22px;
  height: 22px;
  border-radius: 50%;
  background: ${theme.colors.primary};
  flex-shrink: 0;

  svg {
    font-size: 0.6rem;
    color: white;
  }
`;

const SlideContent = styled.div`
  padding: 2.5rem 3rem;

  @media (max-width: ${theme.breakpoints.mobile}) {
    padding: 1.75rem 1.5rem;
  }
`;

const AuthorRow = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  margin-bottom: 1.75rem;
  padding-bottom: 1.5rem;
  border-bottom: 1px solid #f0f0f0;
`;

const QuoteIcon = styled.div`
  color: ${theme.colors.primary};
  font-size: 1.25rem;
  margin-bottom: 1rem;
  opacity: 0.5;
`;

const TestimonialBody = styled.div`
  font-size: 0.98rem;
  line-height: 1.85;
  color: #4b5563;
  white-space: pre-line;

  @media (max-width: ${theme.breakpoints.mobile}) {
    font-size: 0.92rem;
    line-height: 1.75;
  }
`;

const AuthorAvatar = styled.div`
  width: 52px;
  height: 52px;
  border-radius: 50%;
  background: linear-gradient(135deg, ${theme.colors.primary}, #c0392b);
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-weight: 800;
  font-size: 1.1rem;
  flex-shrink: 0;
`;

const AuthorInfo = styled.div`
  flex: 1;
`;

const AuthorName = styled.div`
  font-weight: 700;
  color: ${theme.colors.secondary};
  font-size: 1rem;
  margin-bottom: 0.15rem;
`;

const AuthorMeta = styled.div`
  font-size: 0.85rem;
  color: #64748b;
`;

const AuthorCompany = styled.span`
  color: ${theme.colors.primary};
  font-weight: 600;
`;

const CompanyLogoSmall = styled.img`
  height: 56px;
  max-width: 160px;
  object-fit: contain;
  flex-shrink: 0;
  opacity: 0.8;

  @media (max-width: ${theme.breakpoints.mobile}) {
    display: none;
  }
`;

const NavRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 1rem;
  margin-top: 2rem;
`;

const NavButton = styled.button`
  width: 44px;
  height: 44px;
  border-radius: 50%;
  border: 1.5px solid #d1d5db;
  background: white;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s ease;
  color: ${theme.colors.secondary};

  &:hover {
    border-color: ${theme.colors.primary};
    color: ${theme.colors.primary};
    box-shadow: 0 2px 8px rgba(231, 76, 60, 0.15);
  }

  &:disabled {
    opacity: 0.3;
    cursor: not-allowed;
  }

  svg { font-size: 0.85rem; }
`;

const Dots = styled.div`
  display: flex;
  gap: 0.5rem;
  align-items: center;
`;

const Dot = styled.button`
  width: ${props => props.$active ? '24px' : '8px'};
  height: 8px;
  border-radius: 4px;
  border: none;
  background: ${props => props.$active ? theme.colors.primary : '#d1d5db'};
  cursor: pointer;
  transition: all 0.3s ease;

  &:hover {
    background: ${props => props.$active ? theme.colors.primary : '#94a3b8'};
  }
`;

const HighlightsSection = styled.section`
  padding: 60px 5%;
  background: #ffffff;
`;

const HighlightsInner = styled.div`
  max-width: 900px;
  margin: 0 auto;
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 2rem;

  @media (max-width: ${theme.breakpoints.tablet}) {
    grid-template-columns: 1fr;
    gap: 1.25rem;
  }
`;

const HighlightCard = styled.div`
  text-align: center;
  padding: 2rem 1.5rem;
  border-radius: 16px;
  background: #f8f9fa;
  border: 1px solid #e5e7eb;
  transition: all 0.3s ease;

  &:hover {
    border-color: ${theme.colors.primary};
    box-shadow: 0 8px 24px rgba(231, 76, 60, 0.08);
  }
`;

const HighlightIcon = styled.div`
  width: 48px;
  height: 48px;
  border-radius: 12px;
  background: rgba(231, 76, 60, 0.1);
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 1rem;

  svg {
    font-size: 1.2rem;
    color: ${theme.colors.primary};
  }
`;

const HighlightNumber = styled.div`
  font-size: 1.75rem;
  font-weight: 800;
  color: ${theme.colors.secondary};
  margin-bottom: 0.25rem;
`;

const HighlightLabel = styled.div`
  font-size: 0.88rem;
  color: ${theme.colors.darkGray};
  line-height: 1.5;
`;

const CTASection = styled.section`
  background: linear-gradient(135deg, ${theme.colors.secondary} 0%, #1a1a2e 100%);
  padding: 80px 5%;
  text-align: center;
  position: relative;
  overflow: hidden;
`;

const CTAInner = styled.div`
  max-width: 650px;
  margin: 0 auto;
  position: relative;
  z-index: 2;
`;

const CTATitle = styled.h2`
  font-size: 2rem;
  font-weight: 800;
  color: white;
  margin-bottom: 1rem;
  line-height: 1.3;

  @media (max-width: ${theme.breakpoints.mobile}) { font-size: 1.5rem; }
`;

const CTAText = styled.p`
  font-size: 1.05rem;
  color: rgba(255, 255, 255, 0.75);
  line-height: 1.7;
  margin-bottom: 2rem;
`;

const CTAButtons = styled.div`
  display: flex;
  gap: 1rem;
  justify-content: center;
  flex-wrap: wrap;
`;

const PrimaryButton = styled(Link)`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  background: ${theme.colors.primary};
  color: white;
  padding: 1rem 2rem;
  border-radius: 50px;
  font-size: 1rem;
  font-weight: 700;
  text-decoration: none;
  transition: all 0.3s ease;

  &:hover {
    background: #c0392b;
    transform: translateY(-2px);
    box-shadow: 0 8px 24px rgba(231, 76, 60, 0.35);
  }

  svg { font-size: 0.85rem; }
`;

const SecondaryButton = styled(Link)`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  background: transparent;
  color: white;
  padding: 1rem 2rem;
  border-radius: 50px;
  font-size: 1rem;
  font-weight: 700;
  text-decoration: none;
  border: 1.5px solid rgba(255, 255, 255, 0.3);
  transition: all 0.3s ease;

  &:hover {
    border-color: white;
    background: rgba(255, 255, 255, 0.08);
    transform: translateY(-2px);
  }
`;

const companyLogos = {
  1: belenGemuseLogo,
  2: cbGrosshandelLogo,
  3: dosteliLogo,
  4: pasaLogo
};

const ReferencesPage = () => {
  const { t } = useTranslation();
  const { lang } = useParams();
  const [activeIndex, setActiveIndex] = useState(0);

  const testimonials = [1, 2, 3, 4];
  const stats = ['1', '2', '3', '4'];

  const getInitials = (name) => {
    return name.split(' ').map(n => n[0]).join('').substring(0, 2);
  };

  const handlePrev = () => setActiveIndex(i => Math.max(0, i - 1));
  const handleNext = () => setActiveIndex(i => Math.min(testimonials.length - 1, i + 1));

  const currentTestimonial = testimonials[activeIndex];

  return (
    <>
      <SEO
        titleKey="references.seo.title"
        descriptionKey="references.seo.description"
      />
      <PageContainer>
        <HeroSection>
          <HeroInner>
            <HeroBadge><FaStar /> {t('references.hero.badge')}</HeroBadge>
            <HeroTitle>{t('references.hero.title')}</HeroTitle>
            <HeroSubtitle>{t('references.hero.subtitle')}</HeroSubtitle>
            <StatsBar>
              {stats.map(key => (
                <StatCard key={key}>
                  <StatNumber>{t(`references.hero.stats.${key}.number`)}</StatNumber>
                  <StatLabel>{t(`references.hero.stats.${key}.label`)}</StatLabel>
                </StatCard>
              ))}
            </StatsBar>
          </HeroInner>
        </HeroSection>

        <LogoSection>
          <LogoInner>
            <LogoLabel>{t('references.companies.title')}</LogoLabel>
            <LogoGrid>
              {[1, 2, 3, 4].map(num => (
                <LogoItem key={num}>
                  <img src={companyLogos[num]} alt={t(`references.companies.items.company${num}.name`)} loading="lazy" />
                  <LogoName>{t(`references.companies.items.company${num}.name`)}</LogoName>
                  <LogoCountry>{t(`references.companies.items.company${num}.sector`)}</LogoCountry>
                </LogoItem>
              ))}
            </LogoGrid>
          </LogoInner>
        </LogoSection>

        <TestimonialsSection>
          <TestimonialsInner>
            <SectionHeader>
              <SectionTitle>{t('references.testimonials.title')}</SectionTitle>
              <SectionSubtitle>{t('references.testimonials.subtitle')}</SectionSubtitle>
            </SectionHeader>

            <TestimonialCarousel>
              <TestimonialSlide key={activeIndex}>
                <MicroProofBar>
                  {(t(`references.testimonials.items.testimonial${currentTestimonial}.microProof`, { returnObjects: true }) || []).map((item, idx) => (
                    <MicroProofItem key={idx}>
                      <MicroProofIcon><FaCheck /></MicroProofIcon>
                      {item}
                    </MicroProofItem>
                  ))}
                </MicroProofBar>
                <SlideContent>
                  <AuthorRow>
                    <AuthorAvatar>
                      {getInitials(t(`references.testimonials.items.testimonial${currentTestimonial}.author`))}
                    </AuthorAvatar>
                    <AuthorInfo>
                      <AuthorName>{t(`references.testimonials.items.testimonial${currentTestimonial}.author`)}</AuthorName>
                      <AuthorMeta>
                        {t(`references.testimonials.items.testimonial${currentTestimonial}.position`)} — <AuthorCompany>{t(`references.testimonials.items.testimonial${currentTestimonial}.company`)}</AuthorCompany>
                      </AuthorMeta>
                    </AuthorInfo>
                    <CompanyLogoSmall
                      src={companyLogos[currentTestimonial]}
                      alt={t(`references.testimonials.items.testimonial${currentTestimonial}.company`)}
                      loading="lazy"
                    />
                  </AuthorRow>
                  <QuoteIcon><FaQuoteLeft /></QuoteIcon>
                  <TestimonialBody>
                    {t(`references.testimonials.items.testimonial${currentTestimonial}.text`)}
                  </TestimonialBody>
                </SlideContent>
              </TestimonialSlide>

              <NavRow>
                <NavButton onClick={handlePrev} disabled={activeIndex === 0}>
                  <FaChevronLeft />
                </NavButton>
                <Dots>
                  {testimonials.map((_, i) => (
                    <Dot key={i} $active={i === activeIndex} onClick={() => setActiveIndex(i)} />
                  ))}
                </Dots>
                <NavButton onClick={handleNext} disabled={activeIndex === testimonials.length - 1}>
                  <FaChevronRight />
                </NavButton>
              </NavRow>
            </TestimonialCarousel>
          </TestimonialsInner>
        </TestimonialsSection>

        <HighlightsSection>
          <HighlightsInner>
            <HighlightCard>
              <HighlightIcon><FaCheckCircle /></HighlightIcon>
              <HighlightNumber>{t('references.highlights.1.number')}</HighlightNumber>
              <HighlightLabel>{t('references.highlights.1.label')}</HighlightLabel>
            </HighlightCard>
            <HighlightCard>
              <HighlightIcon><FaCheckCircle /></HighlightIcon>
              <HighlightNumber>{t('references.highlights.2.number')}</HighlightNumber>
              <HighlightLabel>{t('references.highlights.2.label')}</HighlightLabel>
            </HighlightCard>
            <HighlightCard>
              <HighlightIcon><FaCheckCircle /></HighlightIcon>
              <HighlightNumber>{t('references.highlights.3.number')}</HighlightNumber>
              <HighlightLabel>{t('references.highlights.3.label')}</HighlightLabel>
            </HighlightCard>
          </HighlightsInner>
        </HighlightsSection>

        <CTASection>
          <CTAInner>
            <CTATitle>{t('references.cta.title')}</CTATitle>
            <CTAText>{t('references.cta.description')}</CTAText>
            <CTAButtons>
              <PrimaryButton to={`/${lang}/demo`}>
                {t('references.cta.demoButton')} <FaArrowRight />
              </PrimaryButton>
              <SecondaryButton to={`/${lang}/contact`}>
                {t('references.cta.contactButton')}
              </SecondaryButton>
            </CTAButtons>
          </CTAInner>
        </CTASection>
      </PageContainer>
    </>
  );
};

export default ReferencesPage;
